import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Play, Clock, Calendar, Users, Star, Filter, Search,
  BookOpen, Video, Award, Book, Lock, CheckCircle,
  ChevronRight, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_TYPE_ICONS = {
  curso: BookOpen,
  webinar: Video,
  masterclass: Award,
  diplomado: Book,
  taller: BookOpen,
  ponencia: Video
};

const CONTENT_TYPE_COLORS = {
  curso: 'bg-blue-500',
  webinar: 'bg-green-500',
  masterclass: 'bg-purple-500',
  diplomado: 'bg-orange-500',
  taller: 'bg-amber-500',
  ponencia: 'bg-cyan-500'
};

export default function MasterclassesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [contentTypes, setContentTypes] = useState(null);
  const [filters, setFilters] = useState({
    content_type: '',
    category: '',
    is_free: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const token = localStorage.getItem('upim_token');
  const user = token ? JSON.parse(localStorage.getItem('upim_user') || '{}') : null;

  useEffect(() => {
    loadContentTypes();
    loadCatalog();
  }, [filters.content_type, filters.category, filters.is_free]);

  const loadContentTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/educational/content-types`);
      setContentTypes(response.data);
    } catch (error) {
      console.error('Error loading content types:', error);
    }
  };

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.content_type) params.append('content_type', filters.content_type);
      if (filters.category) params.append('category', filters.category);
      if (filters.is_free === 'true') params.append('is_free', 'true');
      if (filters.is_free === 'false') params.append('is_free', 'false');
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '50');

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/educational/catalog?${params.toString()}`, { headers });
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error loading catalog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCatalog();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="masterclasses-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Academia UPIM
            </h1>
            <p className="text-xl text-indigo-200 max-w-2xl mx-auto mb-8">
              Cursos, webinars, master classes y diplomados para profesionales inmobiliarios
            </p>
            
            {/* Content Type Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {[
                { key: 'curso', name: 'Curso', icon: BookOpen },
                { key: 'webinar', name: 'Webinar', icon: Video },
                { key: 'masterclass', name: 'Master Class', icon: Award },
                { key: 'taller', name: 'Taller', icon: BookOpen },
                { key: 'ponencia', name: 'Ponencia', icon: Video },
                { key: 'diplomado', name: 'Diplomado', icon: Book }
              ].map(type => (
                <button
                  key={type.key}
                  onClick={() => setFilters({ ...filters, content_type: filters.content_type === type.key ? '' : type.key })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    filters.content_type === type.key
                      ? 'bg-white text-indigo-900 shadow-lg'
                      : 'bg-white/20 hover:bg-white/30 text-white'
                  }`}
                >
                  <type.icon className="w-4 h-4" />
                  <span className="font-medium">{type.name}</span>
                  {type.key === 'masterclass' && (
                    <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded ml-1">GRATIS</span>
                  )}
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Buscar cursos, webinars, ponentes..."
                  className="w-full pl-12 pr-4 py-3 rounded-full text-gray-900 focus:outline-none focus:ring-4 focus:ring-white/30"
                  data-testid="search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700"
                >
                  Buscar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            
            {filters.content_type && (
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-1">
                {contentTypes?.content_types?.[filters.content_type]?.name}
                <button onClick={() => setFilters({ ...filters, content_type: '' })} className="ml-1 hover:text-indigo-900">✕</button>
              </span>
            )}
            
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setFilters({ ...filters, is_free: filters.is_free === 'true' ? '' : 'true' })}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  filters.is_free === 'true' ? 'bg-green-100 text-green-700' : 'border hover:bg-gray-50'
                }`}
              >
                Gratuitos
              </button>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Todas las categorías</option>
                {contentTypes?.categories?.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No hay contenido disponible</h3>
            <p className="text-gray-500">Pronto tendremos nuevos cursos y webinars para ti.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => {
              const IconComponent = CONTENT_TYPE_ICONS[item.content_type] || Video;
              const colorClass = CONTENT_TYPE_COLORS[item.content_type] || 'bg-gray-500';
              
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer"
                  onClick={() => navigate(`/masterclasses/${item.slug || item.id}`)}
                  data-testid={`content-card-${item.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-gray-100">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                        <IconComponent className="w-16 h-16 text-white/50" />
                      </div>
                    )}
                    
                    {/* Type Badge */}
                    <div className={`absolute top-3 left-3 ${colorClass} text-white px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1`}>
                      <IconComponent className="w-3 h-3" />
                      {item.content_type_info?.name || item.content_type}
                    </div>
                    
                    {/* Live Badge */}
                    {item.is_live && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-md text-xs font-bold animate-pulse">
                        🔴 EN VIVO
                      </div>
                    )}
                    
                    {/* Play Overlay */}
                    {item.youtube_video_id && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-indigo-600 ml-1" />
                        </div>
                      </div>
                    )}
                    
                    {/* Access Lock */}
                    {!item.can_access && !item.is_free && (
                      <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        Solo miembros
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {item.title}
                    </h3>
                    
                    {item.speaker_name && (
                      <p className="text-sm text-gray-500 mt-1">
                        {item.speaker_title ? `${item.speaker_title} ` : ''}{item.speaker_name}
                      </p>
                    )}
                    
                    {item.short_description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {item.short_description}
                      </p>
                    )}
                    
                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-gray-500">
                      {item.scheduled_start && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(item.scheduled_start).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {item.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {item.duration_minutes} min
                        </span>
                      )}
                      {item.total_registrations > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {item.total_registrations}
                        </span>
                      )}
                      {item.average_rating > 0 && (
                        <span className="flex items-center gap-1 text-yellow-500">
                          <Star className="w-4 h-4 fill-current" />
                          {item.average_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                    
                    {/* Price & CTA */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <div>
                        {item.is_free ? (
                          <span className="text-green-600 font-semibold">GRATIS</span>
                        ) : (
                          <div>
                            {item.original_price && item.original_price > item.price && (
                              <span className="text-sm text-gray-400 line-through mr-2">
                                {formatPrice(item.original_price)}
                              </span>
                            )}
                            <span className="text-lg font-bold text-gray-900">
                              {formatPrice(item.price)}
                            </span>
                          </div>
                        )}
                      </div>
                      <button className="flex items-center gap-1 text-indigo-600 font-medium hover:text-indigo-700">
                        Ver más <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Content Types Info Section */}
      {!loading && items.length === 0 && contentTypes && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Tipos de Contenido Educativo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(contentTypes.content_types).map(([key, info]) => {
              const IconComponent = CONTENT_TYPE_ICONS[key] || Video;
              const colorClass = CONTENT_TYPE_COLORS[key] || 'bg-gray-500';
              
              return (
                <div key={key} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className={`w-12 h-12 ${colorClass} rounded-lg flex items-center justify-center mb-4`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{info.description}</p>
                  <div className="space-y-1 text-sm text-gray-500">
                    <p>📅 {info.typical_duration}</p>
                    <p>📚 {info.sessions}</p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-700 mb-2">Incluye:</p>
                    <ul className="space-y-1">
                      {info.includes?.slice(0, 3).map((inc, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
